import { useLocalSessionId } from '@daily-co/daily-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import useParticipantsWithHost from '../../hooks/useParticipantsWithHost';
import useWSMessageSender from '../../hooks/useWSMessageSender';
import useWSTurnState, { TURN_ACTIONS, TURN_PHASES } from '../../hooks/useWSTurnState';
import AnimatedHeader from './AnimatedHeader';
import NextSlideCountdown from './NextSlideCountdown';
import ParticipantCamera from './ParticipantCamera';
import { getPhaseTexts, WELCOME_CAMERA_SCALES } from './slideConfig';
import './SlidesCommon.css';

export default function WelcomeSlideContent({ currentPhase }) {
  const { participantA, participantB } = useParticipantsWithHost();
  const { autoAdvanceIn, welcomeOks } = useWSTurnState();
  const localSessionId = useLocalSessionId();
  const { sendTurnAction } = useWSMessageSender();

  const isWelcomePhase = currentPhase === TURN_PHASES.Welcome;
  const hasTwoParticipants = participantA && participantB;

  // Controla quando mostrar o botão (aguarda 3s após segunda pessoa entrar)
  const [showReadyButton, setShowReadyButton] = useState(false);

  useEffect(() => {
    if (isWelcomePhase && hasTwoParticipants) {
      // Quando tem 2 participantes, espera 3 segundos antes de mostrar o botão
      const timer = setTimeout(() => {
        setShowReadyButton(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
    // Se não tem 2 participantes ou não está na fase WELCOME, esconde o botão
    setShowReadyButton(false);
  }, [isWelcomePhase, hasTwoParticipants]);

  const isCurrentUserReady = useMemo(() =>
    localSessionId && welcomeOks.includes(localSessionId),
    [localSessionId, welcomeOks]
  );

  // Textos dinâmicos baseados na quantidade de participantes
  const texts = useMemo(() => {
    const baseTexts = getPhaseTexts({
      phase: currentPhase,
      participantALabel: participantA?.user_name,
      participantBLabel: participantB?.user_name,
    });

    // Se está na fase WELCOME e não tem 2 participantes, usa textos de "waiting"
    if (isWelcomePhase && !hasTwoParticipants) {
      return {
        title: "You're the first to arrive...",
        subtitle: "Your partner confirmed recently. Please wait a little!",
      };
    }

    // Se está na fase WELCOME e tem 2+ participantes, muda o subtitle
    if (isWelcomePhase && hasTwoParticipants) {
      return {
        title: baseTexts.title,
        subtitle: "Who is ready?",
      };
    }

    return baseTexts;
  }, [currentPhase, participantA, participantB, isWelcomePhase, hasTwoParticipants]);

  // Decide quando mostrar countdown baseado na fase (lógica do frontend)
  // Mostra apenas durante as self introductions individuais (A ou B)
  const shouldShowCountdown = currentPhase === TURN_PHASES.SelfIntroductionsA ||
                               currentPhase === TURN_PHASES.SelfIntroductionsB;

  const countdownSeconds = shouldShowCountdown ? autoAdvanceIn : null;

  const handleReadyClick = () => {
    if (!localSessionId || isCurrentUserReady) return;

    sendTurnAction({
      action: TURN_ACTIONS.WelcomeOk,
      participantId: localSessionId,
    });
  };

  return (
    <div className="slide-container" style={{ position: 'relative' }}>
      {/* Fundo amarelo para TODAY_THEME */}
      <AnimatePresence>
        {currentPhase === TURN_PHASES.TodayTheme && (
          <motion.div
            initial={{ clipPath: "circle(0% at 50% 50%)" }}
            animate={{ clipPath: "circle(150% at 50% 50%)" }}
            exit={{ clipPath: "circle(0% at 50% 50%)" }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "#FBB41C",
              zIndex: 10,
              willChange: "clip-path"
            }}
          />
        )}
      </AnimatePresence>

      <div className="centered-container column-container">
          {/* Folhas de fundo - lado direito */}
          <AnimatePresence>
            {currentPhase === TURN_PHASES.TodayTheme && (
              <motion.div
                key="folhas"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  height: '100%',
                  zIndex: 15
                }}
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%', opacity: 0 }}
                transition={{
                  duration: 1.2,
                  ease: "easeOut",
                  delay: 0.3
                }}
              >
                <img
                  src="/images/folhas-bg.svg"
                  alt="Folhas de fundo"
                  style={{
                    height: '100%',
                    width: 'auto',
                    objectFit: 'cover'
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Guará mascot */}
          <AnimatePresence>
            {currentPhase === TURN_PHASES.TodayTheme && (
              <motion.div
                key="guara"
                style={{
                  position: 'absolute',
                  bottom: -30,
                  left: '15%',
                  transform: 'translateX(-50%)',
                  zIndex: 18,
                  height: '60%',
                  overflow: 'hidden'
                }}
                initial={{ x: '-100%' }}
                animate={{ x: '-30%' }}
                exit={{ x: '-100%', opacity: 0 }}
                transition={{
                  duration: 1.0,
                  ease: "easeOut",
                  delay: 0.1
                }}
              >
                <img
                  src="/images/guara-1.png"
                  alt="Guará mascot"
                  style={{
                    height: '100%',
                    width: 'auto',
                    objectFit: 'contain',
                    objectPosition: 'bottom',
                    transform: 'scaleX(-1)'
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Conteúdo principal */}
          <AnimatePresence>
            {currentPhase === TURN_PHASES.TodayTheme && (
              <motion.div
                key="conteudo"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  zIndex: 20,
                  textAlign: 'center'
                }}
                initial={{ y: '200%', x: '-50%', opacity: 0 }}
                animate={{ y: '-50%', x: '-50%', opacity: 1 }}
                exit={{ y: '100%', x: '-50%', opacity: 0 }}
                transition={{
                  duration: 1.0,
                  ease: "easeOut",
                  delay: 0.5
                }}
              >
                <div style={{
                  color: 'white',
                  fontSize: '2rem',
                  fontWeight: '400',
                  marginBottom: '2rem',
                  fontFamily: 'League Spartan'
                }}>
                  Today&apos;s topic is:
                </div>
                <img
                  src="/images/wh-questions.svg"
                  alt="WH Questions"
                  style={{ width: '100%', height: 'auto' }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Countdown centralizado horizontalmente */}
          <AnimatePresence>
            {countdownSeconds && (
              <div style={{ position: 'absolute', left: '50%', top: '20px', transform: 'translateX(-50%)', zIndex: 10 }}>
                <motion.div
                  initial={{ y: -100 }}
                  animate={{ y: 0 }}
                  exit={{ y: -100 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <NextSlideCountdown
                    key={currentPhase}
                    initialSeconds={countdownSeconds}
                  />
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* AnimatePresence para animar entrada/saída do texto */}
          <AnimatedHeader
            title={texts.title}
            subtitle={texts.subtitle}
            currentPhase={currentPhase}
          />

          {/* Botão "I'm ready" - sempre renderizado na fase WELCOME, mas escondido quando só tem 1 pessoa */}
          {isWelcomePhase && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={showReadyButton ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, delay: showReadyButton ? 0.3 : 0 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                marginTop: '32px',
                visibility: showReadyButton ? 'visible' : 'hidden',
                pointerEvents: showReadyButton ? 'auto' : 'none',
              }}
              >
              {/* Botão */}
              <button
                type="button"
                onClick={handleReadyClick}
                disabled={isCurrentUserReady}
                style={{
                  padding: '12px 32px',
                  fontSize: '18px',
                  fontFamily: 'League Spartan',
                  fontWeight: 600,
                  color: isCurrentUserReady ? '#666' : '#fff',
                  backgroundColor: isCurrentUserReady ? '#ddd' : '#f63135',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isCurrentUserReady ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isCurrentUserReady ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isCurrentUserReady) {
                    e.target.style.backgroundColor = '#d6282d';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrentUserReady) {
                    e.target.style.backgroundColor = '#f63135';
                  }
                }}
              >
                {isCurrentUserReady ? 'All right!' : "I'm ready"}
              </button>
            </motion.div>
          )}

          <div className="welcome-cameras-row">
            <motion.div
              animate={texts.items
                ? { scale: WELCOME_CAMERA_SCALES.minimized, y: 40 }
                : { scale: WELCOME_CAMERA_SCALES.normal, y: 0 }
              }
              transition={{
                type: "spring",
                stiffness: 150,
                damping: 18,
                mass: 0.8,
              }}
            >
              <ParticipantCamera participant={participantA} />
            </motion.div>

            {/* Coluna central fixa */}
            <div className="rules-column">
              <AnimatePresence mode="wait">
                {texts.items && (
                  <motion.div
                    key={currentPhase}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    {texts.items.map((item, index) => (
                      <motion.span
                        key={index}
                        className="white-subtitle"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.4, delay: 2 + index * 2.5 },
                        }}
                        exit={{
                          opacity: 0,
                          y: 20,
                          transition: { duration: 0.3, delay: 0 },
                        }}
                      >
                        {item}
                      </motion.span>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.div
              animate={texts.items
                ? { scale: WELCOME_CAMERA_SCALES.minimized, y: 40 }
                : { scale: WELCOME_CAMERA_SCALES.normal, y: 0 }
              }
              transition={{
                type: "spring",
                stiffness: 150,
                damping: 18,
                mass: 0.8,
                delay: 0.1
              }}
            >
              <ParticipantCamera participant={participantB} />
            </motion.div>
          </div>
      </div>
    </div>
  );
}
